import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useProductBaseUnits } from '../api/use-products';

export function ProductBaseUnitSelect({
  id,
  value,
  onValueChange,
  enabled = true,
}: {
  id: string;
  value: string;
  onValueChange: (code: string) => void;
  enabled?: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const units = useProductBaseUnits(enabled);
  const options = units.data ?? [];
  const locale = i18n.resolvedLanguage ?? i18n.language;
  return (
    <div className="space-y-2">
      <Select
        value={value}
        onValueChange={onValueChange}
        disabled={units.isPending || units.isError}
      >
        <SelectTrigger id={id} aria-busy={units.isPending}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {value && !options.some((unit) => unit.code === value) && (
            <SelectItem value={value}>
              {t(`products.units.${value}`, { defaultValue: value })}
            </SelectItem>
          )}
          {options.map((unit) => (
            <SelectItem key={unit.code} value={unit.code}>
              {unit.translations[locale]?.name ||
                t(`products.units.${unit.code}`, { defaultValue: unit.code })}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {units.isPending && (
        <p role="status" className="text-sm text-muted-foreground">
          {t('common.loading')}
        </p>
      )}
      {units.isError && (
        <div role="alert" className="text-sm text-destructive">
          {t('productUnits.loadError')}
          <Button type="button" variant="outline" size="sm" onClick={() => void units.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      )}
    </div>
  );
}
